<template>
	<div :class="clazz" :title="title ? title : ''" @focus="onFocus" @blur="onBlur" @keydown.delete="onDelete" :tabindex="tabindex">
		<span><sym-icon v-if="hasIcon" :name="icon" class="fd-tag-icon"></sym-icon> <span class="fd-tag-name">{{ tagName }}</span></span>
		<object-link v-if="hasSrcType" :src-id="srcId" :src-type="srcType" :fallback="href" class="fd-tag-link"><sym-icon name="boxed-arrow-right"></sym-icon></object-link>
		<it-link v-else-if="hasLink" :to="href" class="fd-tag-link"><sym-icon name="boxed-arrow-right"></sym-icon></it-link>
	</div>
</template>

<script setup lang="ts">
import {computed} from "vue";
import SymIcon from "../../helpers/SymIcon.vue";
import {injectEnv} from "../../Environment";
import ObjectLink from "@fd/helpers/ObjectLink.vue";
import ItLink from "@/router/ItLink.vue";

const props = defineProps({
	name: {
		type: String,
		required: true,
	},
	href: String,
	srcType: Number,
	srcId: [String, Number],
	icon: String,
	title: String,
	maxlength: {
		type: Number,
		default: 64,
	},
	index: {
		type: Number,
		default: 0,
	},
	tabindex: {
		type: Number,
		default: 0,
	},
	disabled: {
		type: Boolean,
		default: false,
	}
});

const emit = defineEmits(["selected", "unselected", "delete"]);

const tabIndexValue = computed(() => {
	return props.disabled ? null : props.tabindex;
});
const clazz = computed(() => {
	const c = ["fd-tag"];
	if (props.disabled) c.push("fd-tag-disabled");
	return c;
});
const tagName = computed(() => {
	const name = props.name.length <= props.maxlength ? props.name : props.name.substring(0, props.maxlength - 1) + "";
	if (name.length === 0) return "?";
	return name;
})
const hasLink = computed(() => {
	return !!props.href;
});
const hasSrcType = computed(() => {
	return ((typeof props.srcType === "string" && props.srcType.length > 0) || (typeof props.srcType === "number" && props.srcType > 0))
		&& ((typeof props.srcId === "string" && props.srcId.length > 0) || (typeof props.srcId === "number" && props.srcId > 0));
});
const hasIcon = computed(() => {
	return !!props.icon;
});

function onFocus() {
	emit("selected", props.index);
}

function onBlur() {
	emit("unselected", props.index);
}

function onDelete() {
	emit("delete", props.index);
}
</script>

<style>
.fd-tag {
	position: relative;
	display: flex;
	flex-direction: row;
	column-gap: 4px;
	align-items: baseline;
	justify-content: space-between;

	line-height: var(--input-height);
	border-radius: var(--pill-radius);
	padding: 0 6px;

	background-color: var(--pill-bg);
	color: var(--pill-fg);
	box-shadow: var(--pill-shadow);

	user-select: none;
	-webkit-user-select:none;
	cursor: pointer;
	outline: none;

	&:hover {
		background-color: var(--pill-bg-hover);
	}

	&:focus, &:focus-visible {
		background-color: var(--pill-selected-bg);
		color: var(--pill-selected-fg);
		box-shadow: var(--pill-selected-shadow);
		--pill-fg: --pill-selected-fg;

		&:hover {
			background-color: var(--pill-selected-bg-hover);
		}
	}

	&.fd-tag-disabled,
	&.fd-tag-disabled:hover,
	&.fd-tag-disabled:focus,
	&.fd-tag-disabled:focus-visible {
		background-color: var(--pill-disabled-bg);
		color: var(--pill-disabled-fg);
		box-shadow: var(--pill-disabled-shadow);
		--pill-fg: --pill-disabled-fg;
	}
}

.fd-tag-name {}

.fd-tag-icon {
	margin-right: 4px;
}

.fd-tag-link {
	--link-fg: var(--pill-fg);
	--link-fg-hover: var(--pill-fg);
	--link-fg-active: var(--pill-fg);
	opacity: 0.5;

	&:hover {
		opacity: 1;
	}
}
</style>
